<template>
  <div class="sim-page">
    <section class="sim-toolbar">
      <div class="sim-toolbar__copy">
        <div class="sim-toolbar__title">模拟互动</div>
        <div class="sim-toolbar__desc">
          造一批模拟用户，用他们发活动帖参与抽奖，并模拟点赞、评论，方便在没人参与时把活动数据跑通。
          模拟账号以 open_id 前缀 <code>mock_</code> 标识：只会影响它们自己的帖子和互动，「清理模拟数据」也只删这部分，不动真实用户。
        </div>
      </div>
      <div class="sim-toolbar__actions">
        <select v-model.number="campaignId" class="filter-select" @change="reloadPosts">
          <option v-for="item in campaigns" :key="String(item.id)" :value="item.id">
            {{ item.name }}（{{ campaignStatusLabel(item.status) }}）
          </option>
        </select>
        <button class="ghost-button" type="button" :disabled="loading" @click="reloadAll">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
        <button class="danger-button" type="button" :disabled="resetting" @click="resetAll">
          {{ resetting ? "清理中" : "清理模拟数据" }}
        </button>
      </div>
    </section>

    <div v-if="message" class="page-message" :class="{ 'page-message--error': messageError }">{{ message }}</div>

    <div class="sim-grid">
      <section class="sim-card">
        <header class="card-head">
          <h3>模拟用户（{{ mockUsers.length }}）</h3>
          <span class="card-hint">昵称留空会自动生成</span>
        </header>

        <div class="mock-form">
          <input v-model.trim="createForm.nickname" class="filter-input" type="text" maxlength="32" placeholder="昵称（可留空，自动生成）" />
          <div class="avatar-field">
            <div class="avatar-preview">
              <img v-if="createForm.avatarUrl" :src="resolveFileUrl(createForm.avatarUrl)" alt="" />
              <span v-else>＋</span>
            </div>
            <div class="avatar-field__body">
              <label class="upload-button">
                {{ creatingAvatar ? "上传中…" : "上传头像图片" }}
                <input type="file" accept="image/*" :disabled="creatingAvatar" @change="pickCreateAvatar($event)" />
              </label>
              <input
                v-model.trim="createForm.avatarUrl"
                class="filter-input"
                type="text"
                placeholder="或直接贴图片地址（二选一）"
              />
            </div>
          </div>
          <div class="mock-form__footer">
            <div class="quick-counts">
              <button class="ghost-button" type="button" :disabled="creating" @click="createUsers(10)">快速建 10 个</button>
              <button class="ghost-button" type="button" :disabled="creating" @click="createUsers(20)">快速建 20 个</button>
              <button class="ghost-button" type="button" :disabled="creating" @click="createUsers(50)">快速建 50 个</button>
            </div>
            <div class="custom-count">
              <label class="count-field">
                自定义
                <input v-model.number="createForm.count" class="count-input" type="number" min="1" max="50" />
                个
              </label>
              <button class="primary-button" type="button" :disabled="creating" @click="createUsers()">
                {{ creating ? "创建中" : "创建" + (createForm.count || 1) + " 个模拟用户" }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="!mockUsers.length" class="list-state">还没有模拟用户，先创建几个。</div>
        <ul v-else class="mock-list">
          <li v-for="user in mockUsers" :key="String(user.id)" class="mock-item">
            <div class="mock-avatar">
              <img v-if="user.avatarUrl" :src="resolveFileUrl(user.avatarUrl)" alt="" />
              <span v-else>{{ avatarText(user.nickname) }}</span>
            </div>
            <div class="mock-info">
              <div class="mock-name">{{ user.nickname || "模拟用户" }}</div>
              <div class="mock-meta">
                UID {{ user.id }} · 帖子 {{ user.postCount || 0 }} · 赞 {{ user.likeCount || 0 }} · 评 {{ user.commentCount || 0 }}
              </div>
            </div>
            <div class="mock-actions">
              <button class="link-button" type="button" @click="pickUser(user)">用 TA 发帖</button>
              <button class="link-button" type="button" @click="startEdit(user)">编辑</button>
              <button class="link-button link-button--danger" type="button" @click="removeUser(user)">删除</button>
            </div>
            <div v-if="editingUserId === user.id" class="mock-edit">
              <div class="avatar-field">
                <div class="avatar-preview avatar-preview--small">
                  <img v-if="editForm.avatarUrl" :src="resolveFileUrl(editForm.avatarUrl)" alt="" />
                  <span v-else>＋</span>
                </div>
                <div class="avatar-field__body">
                  <label class="upload-button upload-button--small">
                    {{ editUploading ? "上传中…" : "换头像图片" }}
                    <input type="file" accept="image/*" :disabled="editUploading" @change="pickEditAvatar($event)" />
                  </label>
                  <input
                    v-model.trim="editForm.avatarUrl"
                    class="filter-input"
                    type="text"
                    placeholder="或直接贴图片地址（二选一）"
                  />
                </div>
              </div>
              <input
                v-model.trim="editForm.nickname"
                class="filter-input"
                type="text"
                maxlength="32"
                placeholder="昵称"
              />
              <div class="mock-edit__actions">
                <button class="primary-button" type="button" :disabled="editSaving" @click="saveEdit(user)">
                  {{ editSaving ? "保存中" : "保存" }}
                </button>
                <button class="ghost-button" type="button" @click="cancelEdit">取消</button>
              </div>
            </div>
          </li>
        </ul>
      </section>

      <section class="sim-card">
        <header class="card-head">
          <h3>发帖参与抽奖</h3>
          <span class="card-hint">{{ currentCampaignName || "未选择活动" }}</span>
        </header>

        <label class="field">
          <span class="field__label">模拟用户</span>
          <select v-model.number="postForm.mockUserId" class="filter-select">
            <option :value="0">请选择模拟用户</option>
            <option v-for="user in mockUsers" :key="String(user.id)" :value="user.id">
              {{ user.nickname || "模拟用户" }}
            </option>
          </select>
        </label>

        <label class="field">
          <span class="field__label">帖子内容</span>
          <textarea
            v-model="postForm.content"
            class="post-textarea"
            rows="4"
            maxlength="1000"
            placeholder="模拟用户想说的话…（只发图片也可以留空）"
          ></textarea>
        </label>

        <div class="field">
          <span class="field__label">帖子图片（可直接上传，也可以贴地址）</span>
          <div class="post-image-row">
            <label class="upload-button">
              {{ postUploading ? "上传中…" : "上传图片" }}
              <input type="file" accept="image/*" multiple :disabled="postUploading" @change="pickPostImages($event)" />
            </label>
            <span class="field-hint">可一次选多张，上传后自动填到下面；也可以直接贴地址，每行一个</span>
          </div>
          <div v-if="postImageList.length" class="post-image-strip">
            <div v-for="(url, index) in postImageList" :key="url + '-' + index" class="post-image-item">
              <img :src="resolveFileUrl(url)" alt="" />
              <button class="post-image-remove" type="button" title="移除这张" @click="removePostImage(index)">×</button>
            </div>
          </div>
          <textarea
            v-model="postForm.imageText"
            class="post-textarea post-textarea--small"
            rows="3"
            placeholder="/uploads/square/xxx.png"
          ></textarea>
        </div>

        <div class="switch-row">
          <label class="checkbox">
            <input v-model="postForm.submitEntry" type="checkbox" />
            同时提交抽奖报名
          </label>
          <label class="checkbox">
            <input v-model="postForm.autoApprove" type="checkbox" :disabled="!postForm.submitEntry" />
            报名直接通过审核
          </label>
        </div>

        <button class="primary-button primary-button--block" type="button" :disabled="publishing" @click="publishPost">
          {{ publishing ? "发布中" : "发布模拟活动帖" }}
        </button>
        <div class="form-hint">
          模拟帖子直接按审核通过处理，不会送微信内容审核，否则会卡在「审核中」看不到。
        </div>
      </section>
    </div>

    <section class="sim-card">
      <header class="card-head">
        <h3>活动帖子（{{ postTotal }}）</h3>
        <div class="card-head__actions">
          <label class="count-field">
            每次点赞
            <input v-model.number="likeBatch" class="count-input" type="number" min="1" max="50" />
            个
          </label>
          <select v-model="postSort" class="filter-select" @change="reloadPosts">
            <option value="like">按点赞数排序</option>
            <option value="latest">按发布时间排序</option>
          </select>
        </div>
      </header>

      <div class="list-hint">
        每个模拟用户对同一条帖子只能点一次赞：现在有 {{ mockUsers.length }} 个模拟用户，所以一条帖子一次最多加
        {{ mockUsers.length }} 个赞。想要更多热度，先用上面的「创建模拟用户」多建几个（一次最多 50 个）。
      </div>

      <div v-if="postsLoading" class="list-state">正在加载活动帖子…</div>
      <div v-else-if="!posts.length" class="list-state">这个活动下还没有帖子，先在上面用模拟用户发一条。</div>
      <div v-else class="post-list">
        <article v-for="post in posts" :key="String(post.id)" class="post-row">
          <div class="post-media">
            <img v-if="coverUrl(post)" class="post-thumb" :src="coverUrl(post)" alt="" />
            <div v-else class="post-thumb post-thumb--empty">{{ post.mediaType === "video" ? "视频" : "无图" }}</div>
          </div>
          <div class="post-main">
            <div class="post-head">
              <span class="post-id">#{{ post.id }}</span>
              <span class="post-author">{{ post.authorName || "用户" }}（UID {{ post.ownerUserId }}）</span>
              <span v-if="post.mockAuthor" class="tag tag--mock">模拟</span>
              <span class="tag" :class="entryClass(post.entryStatus)">{{ entryLabel(post.entryStatus) }}</span>
              <span class="post-time">{{ formatTime(post.createdAt) }}</span>
            </div>
            <div class="post-content">{{ post.content || "（无正文）" }}</div>
            <div class="post-tags">
              <span class="stat stat--like">赞 {{ post.likeCount || 0 }}</span>
              <span class="stat">评 {{ post.commentCount || 0 }}</span>
              <span class="stat" v-if="post.imageCount">{{ post.imageCount }} 张图</span>
            </div>
          </div>
          <div class="post-side">
            <button class="mini-button" type="button" :disabled="busyPostId === post.id" @click="likePost(post)">
              点赞 +{{ likeBatch }}
            </button>
            <button class="mini-button" type="button" :disabled="busyPostId === post.id" @click="randomComment(post)">
              随机评论
            </button>
            <button class="mini-button mini-button--ghost" type="button" @click="toggleComment(post)">
              {{ commentTargetId === post.id ? "收起" : "自定义评论" }}
            </button>
            <div v-if="commentTargetId === post.id" class="comment-box">
              <select v-model.number="commentForm.mockUserId" class="filter-select">
                <option :value="0">随机模拟用户</option>
                <option v-for="user in mockUsers" :key="String(user.id)" :value="user.id">
                  {{ user.nickname || "模拟用户" }}
                </option>
              </select>
              <input
                v-model="commentForm.content"
                class="filter-input"
                type="text"
                maxlength="200"
                placeholder="评论内容（留空随机生成）"
                @keyup.enter="sendComment(post)"
              />
              <button class="primary-button" type="button" :disabled="busyPostId === post.id" @click="sendComment(post)">
                发送
              </button>
            </div>
            <div
              v-if="rowNotice.postId === post.id && rowNotice.text"
              class="row-notice"
              :class="{ 'row-notice--error': rowNotice.error }"
            >
              {{ rowNotice.text }}
            </div>
          </div>
        </article>
      </div>

      <div v-if="postTotal > postPageSize" class="pager">
        <button class="ghost-button" type="button" :disabled="postPage <= 1" @click="changePage(postPage - 1)">上一页</button>
        <span class="pager-text">第 {{ postPage }} / {{ postTotalPages }} 页 · 共 {{ postTotal }} 条</span>
        <button class="ghost-button" type="button" :disabled="postPage >= postTotalPages" @click="changePage(postPage + 1)">下一页</button>
      </div>
    </section>
  </div>
</template>

<script>
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"
import { fetchActivityCampaigns } from "../api/activity"
import {
  createMockUsers,
  createSimulationPost,
  deleteMockUser,
  fetchMockUsers,
  fetchSimulationPosts,
  resetSimulationData,
  simulatePostComment,
  simulatePostLikes,
  updateMockUser
} from "../api/simulation"
import { uploadAdminImage } from "../api/user"

export default {
  name: "SimulationView",
  data() {
    return {
      loading: false,
      creating: false,
      publishing: false,
      resetting: false,
      postsLoading: false,
      busyPostId: null,
      creatingAvatar: false,
      postUploading: false,
      editUploading: false,
      editSaving: false,
      editingUserId: 0,
      rowNotice: { postId: 0, text: "", error: false },
      campaigns: [],
      campaignId: null,
      mockUsers: [],
      posts: [],
      postTotal: 0,
      postPage: 1,
      postPageSize: 10,
      postSort: "like",
      likeBatch: 10,
      commentTargetId: null,
      createForm: { nickname: "", avatarUrl: "", count: 20 },
      editForm: { nickname: "", avatarUrl: "" },
      postForm: { mockUserId: 0, content: "", imageText: "", submitEntry: true, autoApprove: true },
      commentForm: { mockUserId: 0, content: "" },
      message: "",
      messageError: false
    }
  },
  computed: {
    postImageList() {
      return String(this.postForm.imageText || "")
        .split(/\r?\n/)
        .map(item => item.trim())
        .filter(item => item.length > 0)
    },
    postTotalPages() {
      return Math.max(1, Math.ceil((this.postTotal || 0) / this.postPageSize))
    },
    currentCampaignName() {
      const matched = this.campaigns.find(item => item.id === this.campaignId)
      return matched ? matched.name : ""
    }
  },
  created() {
    this.initPage()
  },
  methods: {
    resolveFileUrl(value) {
      return resolveBackendAssetUrl(value)
    },
    coverUrl(post) {
      if (!post || !post.coverUrl) return ""
      return this.resolveFileUrl(post.coverUrl)
    },
    avatarText(nickname) {
      const text = String(nickname || "").trim()
      return text ? text.slice(0, 1) : "模"
    },
    formatTime(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 16)
    },
    campaignStatusLabel(status) {
      if (status === "RUNNING") return "进行中"
      if (status === "CLOSED") return "已封榜"
      if (status === "SETTLED") return "已开奖"
      if (status === "READY") return "待开始"
      return "草稿"
    },
    entryLabel(status) {
      if (status === "APPROVED") return "报名已通过"
      if (status === "REJECTED") return "报名已驳回"
      if (status === "PENDING") return "报名审核中"
      return "未报名"
    },
    entryClass(status) {
      if (status === "APPROVED") return "tag--pass"
      if (status === "REJECTED") return "tag--rejected"
      if (status === "PENDING") return "tag--reviewing"
      return "tag--none"
    },
    setMessage(text, isError) {
      this.message = text
      this.messageError = !!isError
    },
    async initPage() {
      try {
        const res = await fetchActivityCampaigns()
        this.campaigns = res.data || []
        const running = this.campaigns.find(item => item.status === "RUNNING") || this.campaigns[0]
        this.campaignId = running ? running.id : null
      } catch (error) {
        this.setMessage((error && error.message) || "活动列表加载失败", true)
      }
      await this.reloadAll()
    },
    async reloadAll() {
      await Promise.all([this.reloadMockUsers(), this.reloadPosts()])
    },
    async reloadMockUsers() {
      this.loading = true
      try {
        const res = await fetchMockUsers()
        this.mockUsers = res.data || []
      } catch (error) {
        this.setMessage((error && error.message) || "模拟用户加载失败", true)
      } finally {
        this.loading = false
      }
    },
    async reloadPosts() {
      if (!this.campaignId) {
        this.posts = []
        this.postTotal = 0
        return
      }
      this.postsLoading = true
      try {
        const res = await fetchSimulationPosts({
          campaignId: this.campaignId,
          sort: this.postSort,
          page: this.postPage,
          pageSize: this.postPageSize
        })
        const data = res.data || {}
        this.posts = data.records || []
        this.postTotal = Number(data.total || 0)
      } catch (error) {
        this.setMessage((error && error.message) || "活动帖子加载失败", true)
      } finally {
        this.postsLoading = false
      }
    },
    async createUsers(count) {
      const wanted = Number(count || this.createForm.count || 1)
      this.creating = true
      try {
        const res = await createMockUsers({
          nickname: this.createForm.nickname || undefined,
          avatarUrl: this.createForm.avatarUrl || undefined,
          count: wanted
        })
        const created = Array.isArray(res.data) ? res.data.length : wanted
        this.setMessage(`${res.message || "模拟用户已创建"}（当前共 ${this.mockUsers.length + created} 个）`, false)
        this.createForm.nickname = ""
        await this.reloadMockUsers()
      } catch (error) {
        this.setMessage((error && error.message) || "创建模拟用户失败", true)
      } finally {
        this.creating = false
      }
    },
    async pickCreateAvatar(event) {
      const file = event && event.target && event.target.files ? event.target.files[0] : null
      if (!file) {
        return
      }
      this.creatingAvatar = true
      try {
        const res = await uploadAdminImage(file)
        const url = res && res.data ? res.data.url : ""
        if (url) {
          this.createForm.avatarUrl = url
          this.setMessage("头像已上传，创建出来的模拟用户会直接用它。", false)
        }
      } catch (error) {
        this.setMessage((error && error.message) || "头像上传失败", true)
      } finally {
        this.creatingAvatar = false
        if (event && event.target) {
          event.target.value = ""
        }
      }
    },
    startEdit(user) {
      this.editingUserId = user.id
      this.editForm.nickname = user.nickname || ""
      this.editForm.avatarUrl = user.avatarUrl || ""
    },
    cancelEdit() {
      this.editingUserId = 0
      this.editForm = { nickname: "", avatarUrl: "" }
    },
    async pickEditAvatar(event) {
      const file = event && event.target && event.target.files ? event.target.files[0] : null
      if (!file) {
        return
      }
      this.editUploading = true
      try {
        const res = await uploadAdminImage(file)
        const url = res && res.data ? res.data.url : ""
        if (url) {
          this.editForm.avatarUrl = url
          this.setMessage("新头像已上传，记得点保存。", false)
        }
      } catch (error) {
        this.setMessage((error && error.message) || "头像上传失败", true)
      } finally {
        this.editUploading = false
        if (event && event.target) {
          event.target.value = ""
        }
      }
    },
    async saveEdit(user) {
      const nickname = String(this.editForm.nickname || "").trim()
      const avatarUrl = String(this.editForm.avatarUrl || "").trim()
      if (!nickname && !avatarUrl) {
        this.setMessage("昵称和头像至少填一个", true)
        return
      }
      this.editSaving = true
      try {
        const res = await updateMockUser(user.id, {
          nickname: nickname || undefined,
          avatarUrl: avatarUrl || undefined
        })
        this.setMessage(res.message || "模拟用户已更新", false)
        this.editingUserId = 0
        await this.reloadAll()
      } catch (error) {
        this.setMessage((error && error.message) || "保存失败", true)
      } finally {
        this.editSaving = false
      }
    },
    pickUser(user) {
      this.postForm.mockUserId = user.id
      this.setMessage(`已选中「${user.nickname || "模拟用户"}」，填好内容就能发布。`, false)
    },
    async removeUser(user) {
      if (!window.confirm(`确定删除模拟用户「${user.nickname || user.id}」吗？TA 的模拟帖子、点赞、评论和报名记录会一起清理。`)) {
        return
      }
      try {
        const res = await deleteMockUser(user.id)
        this.setMessage(res.message || "模拟用户已清理", false)
        if (this.postForm.mockUserId === user.id) {
          this.postForm.mockUserId = 0
        }
        await this.reloadAll()
      } catch (error) {
        this.setMessage((error && error.message) || "删除模拟用户失败", true)
      }
    },
    parseImageLines() {
      return String(this.postForm.imageText || "")
        .split(/\r?\n/)
        .map(item => item.trim())
        .filter(item => item.length > 0)
    },
    async pickPostImages(event) {
      const files = event && event.target && event.target.files ? Array.from(event.target.files) : []
      if (!files.length) {
        return
      }
      this.postUploading = true
      try {
        const uploaded = []
        for (const file of files) {
          const res = await uploadAdminImage(file)
          const url = res && res.data ? res.data.url : ""
          if (url) {
            uploaded.push(url)
          }
        }
        if (uploaded.length) {
          this.postForm.imageText = this.postImageList.concat(uploaded).join("\n")
          this.setMessage(`已上传 ${uploaded.length} 张图片，发布后就会带在帖子里。`, false)
        }
      } catch (error) {
        this.setMessage((error && error.message) || "图片上传失败", true)
      } finally {
        this.postUploading = false
        if (event && event.target) {
          event.target.value = ""
        }
      }
    },
    removePostImage(index) {
      this.postForm.imageText = this.postImageList.filter((item, itemIndex) => itemIndex !== index).join("\n")
    },
    async publishPost() {
      if (!this.postForm.mockUserId) {
        this.setMessage("请先选择模拟用户", true)
        return
      }
      const images = this.parseImageLines()
      if (!String(this.postForm.content || "").trim() && images.length === 0) {
        this.setMessage("写点内容或填一张图片地址", true)
        return
      }
      this.publishing = true
      try {
        const res = await createSimulationPost({
          mockUserId: this.postForm.mockUserId,
          campaignId: this.campaignId,
          content: this.postForm.content || "",
          imageUrls: images,
          submitEntry: this.postForm.submitEntry,
          autoApproveEntry: this.postForm.autoApprove
        })
        this.setMessage(res.message || "模拟帖子已发布", false)
        this.postForm.content = ""
        this.postForm.imageText = ""
        this.postPage = 1
        await this.reloadAll()
      } catch (error) {
        this.setMessage((error && error.message) || "发布模拟帖子失败", true)
      } finally {
        this.publishing = false
      }
    },
    async likePost(post) {
      this.busyPostId = post.id
      try {
        const res = await simulatePostLikes(post.id, this.likeBatch || 10)
        const data = res.data || {}
        const text = data.detail || "点赞模拟完成"
        this.setMessage(text, false)
        this.rowNotice = { postId: post.id, text, error: false }
        await this.reloadPosts()
      } catch (error) {
        const text = (error && error.message) || "模拟点赞失败"
        this.setMessage(text, true)
        this.rowNotice = { postId: post.id, text, error: true }
      } finally {
        this.busyPostId = null
      }
    },
    toggleComment(post) {
      this.commentTargetId = this.commentTargetId === post.id ? null : post.id
      this.commentForm.content = ""
      this.commentForm.mockUserId = 0
    },
    async sendComment(post) {
      this.busyPostId = post.id
      try {
        const res = await simulatePostComment(post.id, {
          mockUserId: this.commentForm.mockUserId || undefined,
          content: this.commentForm.content || undefined
        })
        const data = res.data || {}
        const text = data.detail ? `已模拟评论：${data.detail}` : "评论模拟完成"
        this.setMessage(text, false)
        this.rowNotice = { postId: post.id, text, error: false }
        this.commentForm.content = ""
        await this.reloadPosts()
      } catch (error) {
        const text = (error && error.message) || "模拟评论失败"
        this.setMessage(text, true)
        this.rowNotice = { postId: post.id, text, error: true }
      } finally {
        this.busyPostId = null
      }
    },
    async randomComment(post) {
      this.busyPostId = post.id
      try {
        const res = await simulatePostComment(post.id, {})
        const data = res.data || {}
        const text = data.detail ? `已模拟评论：${data.detail}` : "评论模拟完成"
        this.setMessage(text, false)
        this.rowNotice = { postId: post.id, text, error: false }
        await this.reloadPosts()
      } catch (error) {
        const text = (error && error.message) || "模拟评论失败"
        this.setMessage(text, true)
        this.rowNotice = { postId: post.id, text, error: true }
      } finally {
        this.busyPostId = null
      }
    },
    changePage(next) {
      this.postPage = Math.min(Math.max(1, next), this.postTotalPages)
      this.reloadPosts()
    },
    async resetAll() {
      if (!window.confirm("确定清理全部模拟数据吗？会删除所有模拟用户的帖子、报名、点赞和评论；真实用户的数据不受影响。")) {
        return
      }
      this.resetting = true
      try {
        const res = await resetSimulationData()
        this.setMessage(res.message || "模拟数据已清理", false)
        this.postForm.mockUserId = 0
        this.postPage = 1
        await this.reloadAll()
      } catch (error) {
        this.setMessage((error && error.message) || "清理模拟数据失败", true)
      } finally {
        this.resetting = false
      }
    }
  }
}
</script>

<style scoped>
.sim-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.sim-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 24px;
  border-radius: 16px;
  background: linear-gradient(120deg, #fff5e6 0%, #ffffff 62%);
  border: 1px solid #f3e2c7;
}

.sim-toolbar__title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.sim-toolbar__desc {
  margin-top: 8px;
  max-width: 620px;
  font-size: 13px;
  line-height: 20px;
  color: #6b7280;
}

.sim-toolbar__desc code {
  padding: 1px 5px;
  border-radius: 5px;
  background: #f6ede0;
  color: #a1620a;
  font-size: 12px;
}

.sim-toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.sim-toolbar__actions .filter-select {
  max-width: 300px;
  text-overflow: ellipsis;
}

.sim-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 18px;
}

.sim-card {
  padding: 20px 22px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #eceff3;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.card-head h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}

.card-head__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-hint {
  font-size: 12px;
  color: #9ca3af;
}

.filter-select,
.filter-input {
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #dfe3e8;
  font-size: 13px;
  color: #1f2937;
  box-sizing: border-box;
  background: #ffffff;
}

.filter-input {
  flex: 1;
  min-width: 0;
}

.mock-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
}

.mock-form__footer {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.quick-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.custom-count {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.count-field {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4b5563;
}

.count-input {
  width: 62px;
  height: 34px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid #dfe3e8;
  font-size: 13px;
  box-sizing: border-box;
}

.primary-button,
.ghost-button,
.danger-button {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
}

.primary-button {
  background: #111827;
  color: #ffffff;
}

.primary-button--block {
  width: 100%;
  margin-top: 4px;
}

.ghost-button {
  background: #ffffff;
  border-color: #dfe3e8;
  color: #374151;
}

.danger-button {
  background: #fff5f5;
  border-color: #f5c2c7;
  color: #b42318;
}

.primary-button:disabled,
.ghost-button:disabled,
.danger-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.mock-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 420px;
  overflow-y: auto;
}

.mock-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fafbfd;
  border: 1px solid #eef1f5;
}

.avatar-field {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.avatar-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  border: 1px dashed #d7deea;
  background: #f7f9fc;
  color: #b0b7c3;
  font-size: 20px;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar-preview--small {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  font-size: 16px;
}

.avatar-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-field__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.upload-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px dashed #d9c09a;
  background: #fffaf2;
  color: #a1620a;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}

.upload-button input {
  display: none;
}

.upload-button--small {
  height: 30px;
  padding: 0 12px;
  font-size: 12px;
}

.mock-edit {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin-top: 4px;
  padding: 12px;
  border-radius: 12px;
  background: #fffdf8;
  border: 1px solid #f1e4cf;
}

.mock-edit__actions {
  display: flex;
  gap: 10px;
}

.mock-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  background: #ffe8c8;
  color: #a1620a;
  font-size: 15px;
  font-weight: 700;
  overflow: hidden;
  flex-shrink: 0;
}

.mock-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mock-info {
  flex: 1;
  min-width: 0;
}

.mock-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.mock-meta {
  margin-top: 3px;
  font-size: 12px;
  color: #9ca3af;
}

.mock-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.link-button {
  padding: 0;
  border: none;
  background: none;
  color: #2563eb;
  font-size: 13px;
  cursor: pointer;
}

.link-button--danger {
  color: #b42318;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.field__label {
  font-size: 13px;
  color: #4b5563;
}

.post-textarea {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #dfe3e8;
  font-size: 13px;
  line-height: 20px;
  color: #1f2937;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;
}

.post-textarea--small {
  min-height: 62px;
}

.post-image-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.field-hint {
  font-size: 12px;
  line-height: 18px;
  color: #9ca3af;
}

.post-image-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.post-image-item {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 10px;
  border: 1px solid #e8edf3;
  background: #f7f9fc;
  overflow: hidden;
}

.post-image-item img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.post-image-remove {
  position: absolute;
  right: 2px;
  top: 2px;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.62);
  color: #ffffff;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
}

.switch-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
}

.checkbox {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4b5563;
}

.form-hint {
  margin-top: 10px;
  font-size: 12px;
  line-height: 18px;
  color: #9ca3af;
}

.post-list {
  display: flex;
  flex-direction: column;
}

.post-row {
  display: flex;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #f1f3f6;
}

.post-row:last-child {
  border-bottom: none;
}

.post-media {
  flex-shrink: 0;
}

.post-thumb {
  display: block;
  width: 72px;
  height: 72px;
  border-radius: 10px;
  object-fit: cover;
  background: #f3f4f6;
}

.post-thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #9ca3af;
}

.post-main {
  flex: 1;
  min-width: 0;
}

.post-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.post-id {
  font-size: 12px;
  color: #9ca3af;
}

.post-author {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.post-time {
  font-size: 12px;
  color: #b0b7c3;
}

.tag {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: #f1f5f9;
  color: #64748b;
}

.tag--mock {
  background: #fff2dd;
  color: #a1620a;
}

.tag--pass {
  background: #e8f6ec;
  color: #2f7c4d;
}

.tag--reviewing {
  background: #eef2ff;
  color: #4f5bd5;
}

.tag--rejected {
  background: #fdeceb;
  color: #b42318;
}

.tag--none {
  background: #f1f5f9;
  color: #94a3b8;
}

.post-content {
  margin-top: 8px;
  font-size: 13px;
  line-height: 20px;
  color: #374151;
  word-break: break-all;
}

.post-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}

.stat {
  font-size: 12px;
  color: #6b7280;
}

.stat--like {
  color: #d9534f;
}

.post-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  width: 260px;
  flex-shrink: 0;
}

.mini-button {
  min-width: 104px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #111827;
  background: #111827;
  color: #ffffff;
  font-size: 12px;
  cursor: pointer;
}

.mini-button--ghost {
  background: #ffffff;
  color: #374151;
  border-color: #dfe3e8;
}

.mini-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.comment-box {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.comment-box .primary-button {
  width: 100%;
}

.row-notice {
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  background: #eef7f0;
  color: #2f7c4d;
  font-size: 12px;
  line-height: 18px;
  text-align: left;
}

.row-notice--error {
  background: #fdeceb;
  color: #b42318;
}

.list-state {
  padding: 26px 0;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
}

.list-hint {
  margin-bottom: 10px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #fff8ec;
  font-size: 12px;
  line-height: 18px;
  color: #a1620a;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 16px;
}

.pager-text {
  font-size: 13px;
  color: #6b7280;
}

.page-message {
  padding: 12px 16px;
  border-radius: 12px;
  background: #eef7f0;
  color: #2f7c4d;
  font-size: 13px;
}

.page-message--error {
  background: #fdeceb;
  color: #b42318;
}

@media (max-width: 1180px) {
  .sim-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .post-side {
    width: 220px;
  }
}
</style>
